import { Injectable } from '@angular/core';
import { forkJoin, from, Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { ArquivoService } from '../../../../../../../../../services/Arquivo/api-arquivoservice';
import { TipoRegistroFeedback } from '../../enums/TipoRegistroFeedback';
import {
  ArquivosFeedbackSignature,
  ListaArquivosFeedbackSignature
} from '../../models/signature/ArquivosFeedbackSignature';
import {
  ArquivosFeedbackResult,
  ListaArquivosFeedbackResult
} from '../../models/result/ArquivosFeedbackResult';
import { RequisicaoDocumentoApiService } from '../../../../../../../cadastro/shared/services/DocumentRequestController/RequisicaoDocumentoApiService';

export interface ArquivoFeedbackComUrl extends ListaArquivosFeedbackResult {
  urlVisualizacao?: string;
}

export interface IndicadorAnexoRegistro {
  temAnexo: boolean;
  icone: string;
}

@Injectable({
  providedIn: 'root'
})
export class ArquivoRegistroFeedbackDataService {
  private readonly maxArquivos = 10;
  private readonly maxBytesImagem = 10 * 1024 * 1024;
  private readonly maxBytesVideo = 50 * 1024 * 1024;
  private readonly extensoesImagem = ['.jpg', '.jpeg', '.png', '.webp', '.gif'];
  private readonly extensoesVideo = ['.mp4', '.webm', '.mov', '.m4v', '.avi'];
  private readonly cacheAnexosComUrl = new Map<string, Observable<ArquivoFeedbackComUrl[]>>();

  constructor(
    private api: ArquivoService,
    private requisicaoDocumentoApi: RequisicaoDocumentoApiService
  ) {}

  obterArquivosDeClipboard(event: ClipboardEvent): File[] {
    const items = Array.from(event?.clipboardData?.items ?? []);
    const arquivos: File[] = [];

    for (const item of items) {
      if (item.kind !== 'file' || !item.type?.startsWith('image/')) {
        continue;
      }
      const file = item.getAsFile();
      if (file) {
        arquivos.push(this.normalizarArquivoColado(file));
      }
    }

    return arquivos;
  }

  normalizarArquivoColado(file: File): File {
    const tipo = file.type || 'image/png';
    const extensaoPorTipo: Record<string, string> = {
      'image/jpeg': '.jpg',
      'image/jpg': '.jpg',
      'image/png': '.png',
      'image/webp': '.webp',
      'image/gif': '.gif'
    };
    const extensao = extensaoPorTipo[tipo] || this.obterExtensao(file.name) || '.png';
    const nomeAtual = (file.name || '').trim().toLowerCase();
    const nomeGenerico = !nomeAtual
      || nomeAtual === 'image.png'
      || nomeAtual === 'image.jpg'
      || nomeAtual === 'image.jpeg'
      || nomeAtual === 'blob';

    if (!nomeGenerico) {
      return file;
    }

    const agora = new Date();
    const stamp = [
      agora.getFullYear(),
      String(agora.getMonth() + 1).padStart(2, '0'),
      String(agora.getDate()).padStart(2, '0'),
      '-',
      String(agora.getHours()).padStart(2, '0'),
      String(agora.getMinutes()).padStart(2, '0'),
      String(agora.getSeconds()).padStart(2, '0')
    ].join('');

    return new File([file], `print-${stamp}${extensao}`, {
      type: tipo,
      lastModified: file.lastModified || Date.now()
    });
  }

  validarArquivo(file: File): string | null {
    if (!file) {
      return 'Arquivo inválido.';
    }

    const video = this.ehVideo(file);
    const imagem = this.ehImagem(file);
    if (!video && !imagem) {
      return 'Formato não permitido. Use imagens (JPG, PNG, WEBP, GIF) ou vídeos (MP4, WEBM, MOV).';
    }

    const maxBytes = video ? this.maxBytesVideo : this.maxBytesImagem;
    const limite = video ? '50 MB' : '10 MB';
    if (file.size > maxBytes) {
      return `Arquivo muito grande. Tamanho máximo: ${limite}.`;
    }

    return null;
  }

  /** @deprecated Use validarArquivo */
  validarArquivoImagem(file: File): string | null {
    return this.validarArquivo(file);
  }

  ehVideo(file: File): boolean {
    if (file.type?.startsWith('video/')) {
      return true;
    }
    return this.extensoesVideo.includes(this.obterExtensao(file.name));
  }

  ehImagem(file: File): boolean {
    if (file.type?.startsWith('image/')) {
      return true;
    }
    return this.extensoesImagem.includes(this.obterExtensao(file.name));
  }

  ehVideoPorNome(nomeArquivo: string): boolean {
    return this.extensoesVideo.includes(this.obterExtensao(nomeArquivo));
  }

  ehImagemPorNome(nomeArquivo: string): boolean {
    return this.extensoesImagem.includes(this.obterExtensao(nomeArquivo));
  }

  obterIconeAnexos(arquivos: ListaArquivosFeedbackResult[]): string {
    const nomes = arquivos.map(arquivo => arquivo.originalFileName ?? arquivo.OriginalFileName ?? '');
    const temVideo = nomes.some(nome => this.ehVideoPorNome(nome));
    const temImagem = nomes.some(nome => this.ehImagemPorNome(nome));
    if (temVideo && temImagem) {
      return 'attach-2-outline';
    }
    if (temVideo) {
      return 'film-outline';
    }
    return 'image-outline';
  }

  obterIconeTipoAnexo(nomeArquivo: string): string {
    if (this.ehVideoPorNome(nomeArquivo)) {
      return 'film-outline';
    }
    if (this.ehImagemPorNome(nomeArquivo)) {
      return 'image-outline';
    }
    return 'file-outline';
  }

  obterTooltipIndicadorAnexo(icone: string): string {
    switch (icone) {
      case 'film-outline':
        return 'Vídeos em anexo';
      case 'attach-2-outline':
        return 'Imagens e vídeos em anexo';
      default:
        return 'Imagens em anexo';
    }
  }

  obterTooltipTipoAnexo(nomeArquivo: string): string {
    if (this.ehVideoPorNome(nomeArquivo)) {
      return 'Vídeo';
    }
    if (this.ehImagemPorNome(nomeArquivo)) {
      return 'Imagem';
    }
    return 'Arquivo';
  }

  limparCache(): void {
    this.cacheAnexosComUrl.clear();
  }

  invalidarCacheRegistro(registroId: string): void {
    this.cacheAnexosComUrl.delete(registroId);
  }

  /**
   * Fase rápida: só metadados para ícone na tabela (sem URL do Backblaze).
   */
  precarregarIndicadoresAnexos(registroIds: string[]): Observable<Map<string, IndicadorAnexoRegistro>> {
    const ids = [...new Set(registroIds.filter(Boolean))];
    if (!ids.length) {
      return of(new Map());
    }

    return forkJoin(
      ids.map(id =>
        this.carregarAnexosRegistroOperacional(id).pipe(
          map(result => {
            const arquivos = this.normalizarArquivos(result);
            return {
              id,
              indicador: {
                temAnexo: arquivos.length > 0,
                icone: this.obterIconeAnexos(arquivos)
              } as IndicadorAnexoRegistro
            };
          }),
          catchError(() => of({
            id,
            indicador: { temAnexo: false, icone: 'image-outline' } as IndicadorAnexoRegistro
          }))
        )
      )
    ).pipe(
      map(itens => {
        const mapa = new Map<string, IndicadorAnexoRegistro>();
        itens.forEach(item => mapa.set(item.id, item.indicador));
        return mapa;
      })
    );
  }

  /**
   * Fase completa: URLs para exibir miniaturas ao expandir (com cache por registro).
   */
  precarregarAnexosComUrl(registroIds: string[]): Observable<Map<string, ArquivoFeedbackComUrl[]>> {
    const ids = [...new Set(registroIds.filter(Boolean))];
    if (!ids.length) {
      return of(new Map());
    }

    return forkJoin(
      ids.map(id =>
        this.carregarAnexosComUrl(id).pipe(
          map(anexos => ({ id, anexos })),
          catchError(() => of({ id, anexos: [] as ArquivoFeedbackComUrl[] }))
        )
      )
    ).pipe(
      map(itens => {
        const mapa = new Map<string, ArquivoFeedbackComUrl[]>();
        itens.forEach(item => mapa.set(item.id, item.anexos));
        return mapa;
      })
    );
  }

  private obterExtensao(nomeArquivo: string): string {
    const ponto = nomeArquivo.lastIndexOf('.');
    if (ponto < 0) {
      return '';
    }
    return nomeArquivo.slice(ponto).toLowerCase();
  }

  podeAdicionarMais(quantidadeAtual: number): boolean {
    return quantidadeAtual < this.maxArquivos;
  }

  lerArquivoComoBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.includes(',') ? result.split(',')[1] : result;
        resolve(base64);
      };
      reader.onerror = () => reject(new Error('Não foi possível ler o arquivo.'));
      reader.readAsDataURL(file);
    });
  }

  incluirImagensRegistroOperacional(registroId: string, arquivos: File[]): Observable<any> {
    if (!arquivos?.length) {
      return of(null);
    }

    return from(this.montarListaArquivos(arquivos)).pipe(
      switchMap(lista => {
        const signature: ArquivosFeedbackSignature = {
          RegistroContatoOperacionalId: registroId,
          TipoFeedback: TipoRegistroFeedback.RegistroOperacional,
          DataInclusao: new Date().toISOString(),
          ArquivosFeedback: lista
        };
        return this.api.IncluirArquivoRegistroFeedback(signature);
      })
    );
  }

  incluirAnexosHistoricoExecutivo(clientHistoryId: string, arquivos: File[]): Observable<any> {
    if (!arquivos?.length || !clientHistoryId) {
      return of(null);
    }

    return from(this.montarListaArquivos(arquivos)).pipe(
      switchMap(lista => {
        const signature: ArquivosFeedbackSignature = {
          ClientHistoryId: clientHistoryId,
          TipoFeedback: TipoRegistroFeedback.HistoricoFeedbackExecutivo,
          DataInclusao: new Date().toISOString(),
          ArquivosFeedback: lista
        };
        return this.api.IncluirArquivoRegistroFeedback(signature);
      })
    );
  }

  carregarAnexosRegistroOperacional(registroId: string): Observable<ArquivosFeedbackResult | null> {
    return this.api.CarregarArquivosRegistroFeedback({
      TipoFeedback: TipoRegistroFeedback.RegistroOperacional,
      RegistroContatoOperacionalId: registroId
    }).pipe(
      map(response => {
        const success = response?.success ?? response?.Success;
        const data = response?.data ?? response?.Data;
        return success && data ? data as ArquivosFeedbackResult : null;
      })
    );
  }

  carregarAnexosHistoricoExecutivo(clientHistoryId: string): Observable<ArquivosFeedbackResult | null> {
    return this.api.CarregarArquivosRegistroFeedback({
      TipoFeedback: TipoRegistroFeedback.HistoricoFeedbackExecutivo,
      ClientHistoryId: clientHistoryId
    }).pipe(
      map(response => {
        const success = response?.success ?? response?.Success;
        const data = response?.data ?? response?.Data;
        return success && data ? data as ArquivosFeedbackResult : null;
      })
    );
  }

  /** Lista anexos do histórico executivo via v6/CarregarArquivosRegistroFeedback. */
  carregarAnexosHistorico(clientHistoryId: string): Observable<ArquivoFeedbackComUrl[]> {
    return this.carregarAnexosHistoricoComUrl(clientHistoryId);
  }

  carregarAnexosComUrl(registroId: string): Observable<ArquivoFeedbackComUrl[]> {
    if (!registroId) {
      return of([]);
    }

    const cache = this.cacheAnexosComUrl.get(registroId);
    if (cache) {
      return cache;
    }

    const request$ = this.carregarAnexosRegistroOperacional(registroId).pipe(
      switchMap(result => this.montarAnexosComUrl(result)),
      shareReplay(1)
    );

    this.cacheAnexosComUrl.set(registroId, request$);
    return request$;
  }

  carregarAnexosHistoricoComUrl(clientHistoryId: string): Observable<ArquivoFeedbackComUrl[]> {
    if (!clientHistoryId) {
      return of([]);
    }

    const cacheKey = `historico:${clientHistoryId}`;
    const cache = this.cacheAnexosComUrl.get(cacheKey);
    if (cache) {
      return cache;
    }

    const request$ = this.carregarAnexosHistoricoExecutivo(clientHistoryId).pipe(
      switchMap(result => this.montarAnexosComBlobViaApi(result)),
      shareReplay(1)
    );

    this.cacheAnexosComUrl.set(cacheKey, request$);
    return request$;
  }

  invalidarCacheHistorico(clientHistoryId: string): void {
    this.cacheAnexosComUrl.delete(`historico:${clientHistoryId}`);
  }

  private montarAnexosComUrl(result: ArquivosFeedbackResult | null): Observable<ArquivoFeedbackComUrl[]> {
    const arquivos = this.normalizarArquivos(result);
    if (!arquivos.length) {
      return of([]);
    }

    const requests = arquivos.map(arquivo => {
      const filePath = arquivo.filePath ?? arquivo.FilePath;
      if (!filePath) {
        return of({ ...arquivo, urlVisualizacao: undefined });
      }
      return this.requisicaoDocumentoApi.ObterUrlDownloadArquivo(filePath).pipe(
        map(res => {
          const payload = res?.data ?? res?.Data ?? res;
          const url = payload?.downloadUrl ?? payload?.DownloadUrl;
          const token = payload?.authorizationToken ?? payload?.AuthorizationToken;
          const urlVisualizacao = url && token ? `${url}?Authorization=${encodeURIComponent(token)}` : url;
          return { ...arquivo, urlVisualizacao };
        }),
        catchError(() => of({ ...arquivo, urlVisualizacao: undefined }))
      );
    });

    return forkJoin(requests);
  }

  /** Conteúdo via API (base64) — miniatura sem expor URL do Backblaze no browser. */
  private montarAnexosComBlobViaApi(result: ArquivosFeedbackResult | null): Observable<ArquivoFeedbackComUrl[]> {
    const arquivos = this.normalizarArquivos(result);
    if (!arquivos.length) {
      return of([]);
    }

    const requests = arquivos.map(arquivo => {
      const filePath = arquivo.filePath ?? arquivo.FilePath;
      if (!filePath) {
        return of({ ...arquivo, urlVisualizacao: undefined });
      }

      return this.obterBlobUrlVisualizacao(filePath).pipe(
        map(urlVisualizacao => ({ ...arquivo, urlVisualizacao }))
      );
    });

    return forkJoin(requests);
  }

  private obterBlobUrlVisualizacao(filePath: string): Observable<string | undefined> {
    return this.requisicaoDocumentoApi.BaixarArquivoSolicitacao(filePath).pipe(
      map((response: any) => {
        const success = response?.success ?? response?.Success;
        const data = response?.data ?? response?.Data;
        const base64 = data?.conteudoBase64 ?? data?.ConteudoBase64;
        const contentType = data?.contentType ?? data?.ContentType ?? 'application/octet-stream';
        if (!success || !base64) {
          return undefined;
        }
        return this.criarBlobUrl(base64, contentType);
      }),
      catchError(() => of(undefined))
    );
  }

  private criarBlobUrl(base64: string, contentType: string): string {
    let conteudo = base64.trim();
    const separador = conteudo.indexOf(',');
    if (separador >= 0 && conteudo.slice(0, separador).includes('base64')) {
      conteudo = conteudo.slice(separador + 1);
    }

    const byteCharacters = atob(conteudo);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const blob = new Blob([new Uint8Array(byteNumbers)], { type: contentType });
    return window.URL.createObjectURL(blob);
  }

  deletarAnexo(id: string): Observable<any> {
    return this.api.DeletarArquivosRegistroFeedback(id);
  }

  /**
   * Download via API (base64) — evita abrir link do Backblaze em nova aba.
   */
  baixarAnexo(anexo: ArquivoFeedbackComUrl): Observable<void> {
    const filePath = anexo?.filePath ?? anexo?.FilePath;
    const nomeDownload = anexo?.originalFileName ?? anexo?.OriginalFileName ?? 'imagem';

    if (!filePath) {
      return throwError(() => new Error('Arquivo indisponível para download.'));
    }

    return this.requisicaoDocumentoApi.BaixarArquivoSolicitacao(filePath).pipe(
      tap(response => {
        const success = response?.success ?? response?.Success;
        const data = response?.data ?? response?.Data;
        if (!success || !data) {
          throw new Error('Falha ao baixar arquivo.');
        }

        const base64 = data.conteudoBase64 ?? data.ConteudoBase64;
        const contentType = data.contentType ?? data.ContentType ?? 'application/octet-stream';
        if (!base64) {
          throw new Error('Conteúdo do arquivo não retornado.');
        }

        this.dispararDownloadBase64(base64, nomeDownload, contentType);
      }),
      map(() => void 0),
      catchError(error => throwError(() => error))
    );
  }

  private dispararDownloadBase64(base64: string, nomeArquivo: string, contentType: string): void {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const blob = new Blob([new Uint8Array(byteNumbers)], { type: contentType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = nomeArquivo;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  private async montarListaArquivos(arquivos: File[]): Promise<ListaArquivosFeedbackSignature[]> {
    const lista: ListaArquivosFeedbackSignature[] = [];
    for (const arquivo of arquivos) {
      const conteudoBase64 = await this.lerArquivoComoBase64(arquivo);
      lista.push({
        OriginalFileName: arquivo.name,
        ConteudoBase64: conteudoBase64
      });
    }
    return lista;
  }

  private normalizarArquivos(result: ArquivosFeedbackResult | null): ListaArquivosFeedbackResult[] {
    if (!result) {
      return [];
    }
    return (result.arquivos ?? result.Arquivos ?? []) as ListaArquivosFeedbackResult[];
  }
}
